import {runInAction, toJS} from "mobx";
import langstore from "System/i18n/translator";
import {generateHmacHeaders} from "System/utilities/hmac";

const videoBlobs = new Map();

const ASSEMBLER_EDIT_STATUS_TRANSITIONS = {
    0: 1,
    1: 2,
    2: 1,
    5: 2,
    90: 90
};

const damageReportRetrieveDetailsByUrlParams = async function () {

    const { orderYear, orderNumber, orderId, packageId, componentId } = this.routeParamsCurrent;

    const params = {
        orderYear,
        orderNumber,
        orderId,
        packageId,
        componentId
    };

    await this.damageReportGetAllData(params);
};

const damageReportGetAllData = async function (params) {
    // retrieve data only once
    const { orderYear, orderNumber, orderId, packageId, componentId, rowId, packageProg, skipGlobalLoading } = params;
    if (
        this.damageReport &&
        this.damageReport.components.length > 0 &&
        this.damageReport.selected.orderID === `${orderYear}-${orderNumber}-${orderId}` &&
        this.damageReport.selected.packageID === packageId &&
        typeof rowId === 'undefined' &&
        typeof packageProg === 'undefined'
    ) {
        console.info('Damage data already loaded, skip');
        return {
            components: toJS(this.damageReport.components),
            damages: toJS(this.damageReport.damages),
            damageTypes: toJS(this.damageReport.damageTypes),
            selected: toJS(this.damageReport.selected)
        };
    }

    const loadingKey = 'damageReportGetAllData';

    if (!skipGlobalLoading) this.loadingAdd(loadingKey);

    const url = this.config.paths.apiURL + 'segnalazione-danni/getpackagealldata';

    const _params = {
        orderYear,
        orderNumber,
        orderId,
        packageId,
        rowId,
        packageProg,
        userId: this.userInfo?.c_utente ?? ''
    };

    const response = await this.dataLayer({
        url: url,
        params: _params,
        cacheAge: 0,
        hmac: true,
    });

    let result = null;
    runInAction(() => {
        if (response?.data) {
            const data = toJS(response.data);
            const selectedComponent = data.components.find(c => c.n_sequenza_esploso === Number(componentId));
            this.damageReport = {
                ...this.damageReport,
                components: data.components || null,
                damages: data.damages || null,
                damageTypes: data.damageTypes || null,
                selected: {
                    ...this.damageReport.selected,
                    orderID: data.components?.[0]?.key_ordine || null,
                    packageID: data.components?.[0]?.c_collo || null,
                    component: componentId ? selectedComponent : null,
                }
            };
            result = data;
        }
        if (!skipGlobalLoading) this.loadingRemove(loadingKey);
    });

    return result;
};


const damageReportGetOrderRows = async function (explicitParams = null) {
    let loadingKey = 'damageReportGetOrderRows';
    this.loadingAdd(loadingKey);

    const url = this.config.paths.apiURL + 'segnalazione-danni/getorderrows';

    const params = {
        n_anno: explicitParams?.n_anno ?? Number(this.routeParamsCurrent.orderYear),
        c_numeratore: explicitParams?.c_numeratore ?? this.routeParamsCurrent.orderNumber,
        n_ordine: explicitParams?.n_ordine ?? Number(this.routeParamsCurrent.orderId),
        forceResponse: Boolean(explicitParams?.n_anno && explicitParams?.c_numeratore && explicitParams?.n_ordine)
    };

    const response = await this.dataLayer({
        url: url,
        params: params,
        cacheAge: 0, // 180000, // 3 minutes
        userToken: this.userToken
    });

    // disable new full report if already inserted
    if (response?.data?.result === 'ALREADY_INSERTED') {
        runInAction(() => {
            this.damageReport.orderRows = [];
        });
        this.loadingRemove(loadingKey);
        return false;
    }

    if (response?.data?.length > 0) {

        // check if assembler can add damage report
        await this.damageReportAssemblerEditStatus(params);

        // get data to retrieve Pratica CO
        const praticaCo = await this.getPraticheCo(params);

        // get all damages for this orderId
        await this.damageReportGetDamagesByOrder(params);

        // set orderId on state machine
        this.damageReportSetSelectOrderId(`${params.n_anno}-${params.c_numeratore}-${params.n_ordine}`);

        runInAction(() => {
                this.damageReport.orderRows = response?.data;
                this.damageReport.praticaCo = praticaCo;
        });
    }

    this.loadingRemove(loadingKey);
};

const damageReportGetDamageTypes = async function () {
    const url = this.config.paths.apiURL + 'segnalazione-danni/getDamageTypes';


    const response = await this.dataLayer({
        url: url,
        params: null,
        cacheAge: 0,
        userToken: this.userToken
    });

    let data = [];
    runInAction(() => {
        if (response?.data) {
            data = toJS(response.data);
            this.damageReport.damageTypes = data;
        }
    });

    return data;

};


const damageReportGetComponents = async function (packageId, rowId = '', packageProg = '') {
    const [orderYear, orderNumber, orderId] = this.damageReport.selected.orderID.split('-');
    const url = this.config.paths.apiURL + 'segnalazione-danni/getpackagedata';

    const params = {
        orderYear,
        orderNumber,
        orderId,
        packageId,
        rowId,
        packageProg,
        userId: this.userInfo?.c_utente ?? ''
    };

    const response = await this.dataLayer({
        url: url,
        params: params,
        cacheAge: 0,
        userToken: this.userToken
    });

    let data = [];
    runInAction(() => {
        if (response?.data) {
            data = toJS(response.data);
            this.damageReport.components = data;
        }
    });

    return data;

};

const damageReportGetDamagesByOrder = async function (params) {
    // let loadingKey = 'damageReportGetDamagesByOrder';
    // this.loadingAdd(loadingKey);

    const url = this.config.paths.apiURL + 'segnalazione-danni/getdamagesbyorder';

    const _params = {
        n_anno: Number(params.n_anno),
        c_numeratore: params.c_numeratore,
        n_ordine: Number(params.n_ordine)
    }

    const response = await this.dataLayer({
        url: url,
        params: _params,
        cacheAge: 0,
        userToken: this.userToken
    });

    runInAction(() => {
        if (response?.data) {
            const data = toJS(response.data);
            this.damageReport = {
                ...this.damageReport,
                damages: data,
                selected: {
                    ...this.damageReport.selected,
                    orderID: `${params.n_anno}-${params.c_numeratore}-${params.n_ordine}`
                }
            }
        }
    });

    // this.loadingRemove(loadingKey);

};

const damageReportAssemblerEditStatus = async function (params) {

    const url = this.config.paths.apiURL + 'segnalazione-danni/getdamageseditstatus';

    const _params = {
        n_anno: Number(params.n_anno),
        c_numeratore: params.c_numeratore,
        n_ordine: Number(params.n_ordine)
    }

    const response = await this.dataLayer({
        url: url,
        params: _params,
        cacheAge: 0,
        userToken: this.userToken
    });

    if (response.status === 'OK') {
        runInAction( () => {
           this.damageReport.assemblerEditDisabled = response.data?.fg_segnalazione_montatori_bloccata || 0
        });
    }
}

const damageReportAssemblerEditStatusToggler = async function ()
{
    const url = this.config.paths.apiURL + 'segnalazione-danni/setdamageseditstatus';

    const currentStatus = this.damageReport.assemblerEditDisabled;
    const nextStatus = ASSEMBLER_EDIT_STATUS_TRANSITIONS[currentStatus] ?? currentStatus;

    const [orderYear, orderNumber, orderId] = this.damageReport.selected.orderID.split('-');
    const params = {
        n_anno: Number(orderYear),
        c_numeratore: orderNumber,
        n_ordine: Number(orderId),
        editStatus: nextStatus
    };

    const response = await this.dataLayer({
        url: url,
        params: params,
        cacheAge: 0,
        userToken: this.userToken
    });

    if (response.status === 'OK') {
        runInAction( () => {
            this.damageReport.assemblerEditDisabled = params.editStatus
        });
    }
}

const damageReportSetSelectOrderId = function (orderId) {
    runInAction(() => {
        this.damageReport.selected.orderID = orderId;
    });
};


const damageReportSetSelectPackageId = function (packageId) {
    runInAction(() => {
        this.damageReport.selected.packageID = packageId;
    });
};

const damageReportSetSelectComponent = function (component) {
    runInAction(() => {
        this.damageReport.selected.component = component;
    });
};

const damageReportResetToEmpty = function () {
    runInAction(() => {
        this.damageReport.selected.component = null;
        this.damageReport.selected.damageType = null;
        this.damageReport.praticaCo = [];
        this.damageReport.photos = [];
        this.damageReport.videos.forEach(v => URL.revokeObjectURL(v.objectUrl));
        this.damageReport.videos = [];
        videoBlobs.clear();
        this.damageReport.notes = '';
        this.damageReport.editMode = false;
        this.damageReport.assemblerEditDisabled = 0;
        this.damageReport.editReportId = null;
        this.damageReport.originalReport = null;
        // this.damageReport.orderRows = [];
    });
};

const damageReportSetDamageType = function (damageType) {
    runInAction(() => {
        this.damageReport.selected.damageType = damageType;
    });
};

const damageReportAddPhoto = function (photoDataUrl) {
    runInAction(() => {
        this.damageReport.photos.push({
            id: Date.now(),
            dataUrl: photoDataUrl,
            timestamp: new Date().toISOString(),
        });
    });
};

const damageReportRemovePhoto = function (photoId) {
    runInAction(() => {
        this.damageReport.photos = this.damageReport.photos.filter(
            photo => photo.id !== photoId
        );
    });
};

const damageReportAddVideo = function (blob, mimeType) {
    const id = Date.now();
    const objectUrl = URL.createObjectURL(blob);
    videoBlobs.set(id, blob);
    runInAction(() => {
        this.damageReport.videos.push({ id, objectUrl, mimeType, timestamp: new Date().toISOString() });
    });
};

const damageReportRemoveVideo = function (videoId) {
    const video = this.damageReport.videos.find(v => v.id === videoId);
    if (video) URL.revokeObjectURL(video.objectUrl);
    videoBlobs.delete(videoId);
    runInAction(() => {
        this.damageReport.videos = this.damageReport.videos.filter(v => v.id !== videoId);
    });
};

const damageReportUploadVideos = async function ({ orderYear, orderNumber, orderId, packageId, component, videos }) {
    const url = this.config.paths.apiURL + 'segnalazione-danni/setdamagevideo';
    const hmacHeaders = await generateHmacHeaders(this.config?.hmac?.sharedSecret || '');
    const headers = { ...hmacHeaders };
    if (this.userToken) headers['x-access-token'] = this.userToken;

    for (const video of videos) {
        const blob = videoBlobs.get(video.id);
        if (!blob) continue;
        const ext = video.mimeType.includes('mp4') ? 'mp4' : 'webm';
        const formData = new FormData();
        formData.append('data', JSON.stringify({
            orderYear,
            orderNumber,
            orderId,
            packageId,
            rowNum: component.n_riga,
            progNum: component.n_progressivo,
            seqNum: component.n_sequenza_esploso,
            ds_output: component.ds_output,
            mimeType: video.mimeType,
            userId: this.userInfo.c_utente     //  "c_utente": "11711"
        }));
        formData.append('video', blob, `video_${video.id}.${ext}`);
        await fetch(url, { method: 'POST', headers, body: formData });
    }
};

const damageReportRemoveComponentReport = async function (params) {

    const [orderYear, orderNumber, orderId] = params.key_ordine.split('-');
    const packageId = params.c_collo;
    const _params = {
        orderYear,
        orderNumber,
        orderId,
        packageId,                         // "c_collo": "B85YRB",
        rowNum: params.n_riga,             // "n_riga": 2,
        progNum: params.n_progressivo,     // "n_progressivo": 1,
        seqNum: params.n_sequenza_esploso, // "n_sequenza_esploso": 20
        userId: this.userInfo.c_utente     //  "c_utente": "11711"
    }

    const url = this.config.paths.apiURL + 'segnalazione-danni/deletedamagereport';

    const response = await this.dataLayer({
        url: url,
        params: _params,
        cacheAge: 0,
        userToken: this.userToken
    });

    if (response.status === 'OK' && response.data?.[0]?.result === 'DELETE') {

        const deletedId = response.data[0].id;
        runInAction(() => {
            this.damageReport.damages = this.damageReport.damages.filter(
                damage => damage.id !== deletedId
            );
        });

        // manage error toast
        this.notificationAdd(
            langstore.t('damage_report_deleted', 'Segnalazione eliminata con successo'),
            'damage_deleted',
            10000,
            'warning'
        );
    }
};

const damageReportSetNotes = function (notes) {
    runInAction(() => {
        this.damageReport.notes = notes;
    });
};

const damageReportSubmit = async function (isStandalone = false) {

    const url = this.config.paths.apiURL + 'segnalazione-danni/setdamagereport';

    const [orderYear, orderNumber, orderId] = this.damageReport.selected.orderID.split('-');
    const packageId = this.damageReport.selected.packageID;
    const _params = {
        orderYear: Number(orderYear),
        orderNumber: orderNumber,
        orderId: Number(orderId),
        packageId,
        component: toJS(this.damageReport.selected.component),
        damageType: this.damageReport.selected.damageType.id_causa,
        notes: this.damageReport.notes,
        photos: toJS(this.damageReport.photos),
        userId: this.userInfo?.c_utente || null,
    };

    const response = await this.dataLayer({
        url: url,
        params: _params,
        cacheAge: 0,
        userToken: this.userToken,
        hmac: true,
    });

    if (response.status === 'OK') {
        const videos = toJS(this.damageReport.videos);
        if (videos.length > 0) {
            await this.damageReportUploadVideos({
                orderYear: _params.orderYear,
                orderNumber: _params.orderNumber,
                orderId: _params.orderId,
                packageId: _params.packageId,
                component: _params.component,
                videos,
            });
        }
        this.damageReportResetToEmpty();
        if (isStandalone) {
            await this.damageReportGetDamagesByPackage();
        }
        else {
            await this.damageReportGetDamagesByOrder({
                n_anno: _params.orderYear,
                c_numeratore: _params.orderNumber,
                n_ordine: _params.orderId
            });
        }

        if (isStandalone) {
            // ASSEMBLER case
            this.notificationAdd(
              langstore.t('damage_report_entered_by_assembler', 'Segnalazione inserita con successo'),
              'damage_entered',
              10000,
              'success'
            );
        }
        else {
            // RESELLER case
            this.notificationAdd(
              langstore.t('damage_report_entered', 'Danno compilato con successo. Per invio premi "INVIA SEGNALAZIONE"'),
              'damage_entered',
              10000,
              'warning'
            );
        }
    }
};


const damageReportSubmitToSupport = async function () {
    let loadingKey = 'damageReportSubmitToSupport';
    this.loadingAdd(loadingKey);

    const url = this.config.paths.apiURL + 'segnalazione-danni/setdamagereporttosupport';

    const [orderYear, orderNumber, orderId] = this.damageReport.selected.orderID.split('-');
    const _params = {
        orderYear,
        orderNumber,
        orderId
    }

    const response = await this.dataLayer({
        url: url,
        params: _params,
        cacheAge: 0,
        userToken: this.userToken
    });

    if (response.status === 'OK') {
        this.damageReportResetToEmpty();

        this.notificationAdd(
          langstore.t('damage_full_report_updated', 'Segnalazione inviata con successo'),
          'damage_updated',
          10000,
          'success'
        );

        const route = this.routes.find(route => route.id==='ordini-detail')
        const path = route.path
          .replace(':orderYear', orderYear)
          .replace(':orderNumber', orderNumber)
          .replace(':orderId', orderId);

        this.routeChange(path);
    }
    this.loadingRemove(loadingKey);

};

const damageReportGetDamagesByPackage = async function () {
    let loadingKey = 'damageReportGetDamagesByPackage';
    this.loadingAdd(loadingKey);

    const url = this.config.paths.apiURL + 'segnalazione-danni/getdamagesbypackage';

    const [orderYear, orderNumber, orderId] = this.damageReport.selected.orderID.split('-');
    const _params = {
        orderYear,
        orderNumber,
        orderId,
        packageId: this.damageReport.selected.packageID
    }

    const response = await this.dataLayer({
        url: url,
        params: _params,
        cacheAge: 0,
        hmac: true,
    });

    runInAction(() => {
        if (response?.data) {
            this.damageReport.damages = toJS(response.data)
        }
    });

    this.loadingRemove(loadingKey);
}

const damageReportGetDamagesByComponents = async function (params) {
    const url = this.config.paths.apiURL + 'segnalazione-danni/getdamagesbycomponent';

    const _params = {
        anno: Number(params.anno),
        numeratore: params.numeratore,
        ordine: Number(params.ordine),
        collo: params.collo || '',
        riga: Number(params.riga || 0),
        progressivo: Number(params.progressivo || 0),
        sequenza_esploso: Number(params.sequenza_esploso || 0)
    };

    const response = await this.dataLayer({
        url: url,
        params: _params,
        cacheAge: 0,
        userToken: this.userToken
    });

    let data = [];
    if (response?.status === 'OK' && response?.data) {
        data = toJS(response.data);
    }

    return data;
};

const damageReportLoadComponentReport = async function (reportData) {

    this.damageReportSetSelectPackageId(reportData.c_collo);
    this.damageReportSetSelectComponent(reportData);

    await this.damageReportGetDamageTypes();

    const [anno, numeratore, ordine] = reportData.key_ordine.split('-');

    const res = await this.damageReportGetDamagesByComponents({
        anno,
        numeratore,
        ordine,
        collo: reportData.c_collo,
        riga: reportData.n_riga,
        progressivo: reportData.n_progressivo,
        sequenza_esploso: reportData.n_sequenza_esploso
    });

    if (res && res.length > 0) {
        const report = res[0];
        const photoItems = [];
        const videoItems = [];

        if (report.photos && report.photos.length > 0) {
            await Promise.all(report.photos.map(async (p, idx) => {
                const base64 = p.base64 || '';
                if (base64.startsWith('data:video/')) {
                    const mimeType = base64.match(/^data:([^;]+)/)?.[1] || 'video/webm';
                    try {
                        const blob = await fetch(base64).then(r => r.blob());
                        const id = Date.now() + idx;
                        const objectUrl = URL.createObjectURL(blob);
                        videoBlobs.set(id, blob);
                        videoItems.push({ id, objectUrl, mimeType, fileName: p.fileName, timestamp: new Date().toISOString(), isExisting: true });
                    } catch (e) {
                        console.error('Cannot convert video base64 to objectUrl:', e);
                    }
                } else {
                    photoItems.push({
                        id: Date.now() + idx,
                        dataUrl: base64,
                        fileName: p.fileName,
                        isExisting: true,
                        timestamp: new Date().toISOString()
                    });
                }
            }));
        }

        runInAction(() => {
            this.damageReport.photos = photoItems;
            this.damageReport.videos = videoItems;
            this.damageReport.notes = report.ds_note || '';

            const selectedDamageType = this.damageReport.damageTypes?.find(
                t => t.id_causa === report.id_causa_reclamo_montatore
            );
            this.damageReport.selected.damageType = selectedDamageType || null;

            this.damageReport.editMode = true;
            this.damageReport.editReportId = report.id;
            this.damageReport.originalReport = {
                damageType: selectedDamageType || null,
                notes: report.ds_note || '',
                photoFileNames: photoItems.map(p => p.fileName),
                videoFileNames: videoItems.map(v => v.fileName),
            };
        });
    }
};

const damageReportUpdate = async function (isStandalone = false) {
    const { editReportId, originalReport, selected, photos, videos, notes } = this.damageReport;

    const currentPhotoFileNames = photos
        .filter(p => p.isExisting)
        .map(p => p.fileName);

    const photosToAdd = photos.filter(p => !p.isExisting);
    const photosToRemove = originalReport.photoFileNames.filter(
        fileName => !currentPhotoFileNames.includes(fileName)
    );

    const currentVideoFileNames = videos
        .filter(v => v.isExisting)
        .map(v => v.fileName);

    const videosToAdd = videos.filter(v => !v.isExisting);
    const videosToRemove = originalReport.videoFileNames.filter(
        fileName => !currentVideoFileNames.includes(fileName)
    );

    const damageTypeChanged = selected.damageType?.id_causa !== originalReport.damageType?.id_causa;
    const notesChanged = (notes ?? '') !== (originalReport.notes ?? '');
    const photosChanged = photosToAdd.length > 0 || photosToRemove.length > 0;
    const videosChanged = videosToAdd.length > 0 || videosToRemove.length > 0;

    if (!damageTypeChanged && !notesChanged && !photosChanged && !videosChanged) {
        this.modalClose();
        return;
    }

    const url = this.config.paths.apiURL + 'segnalazione-danni/updatedamagereport';

    const _params = {
        keyOrderId: selected.orderID,
        reportId: editReportId,
        userId: this.userInfo.c_utente,
        component: toJS(this.damageReport.selected.component) || {}
    };

    if (damageTypeChanged) _params.damageType = selected.damageType.id_causa;
    if (notesChanged) _params.notes = notes;
    if (photosToAdd.length > 0) _params.photosToAdd = toJS(photosToAdd).map(p => ({ id: Date.now(), dataUrl: p.dataUrl, timestamp: new Date().toISOString() }));
    const allFileNamesToRemove = [...photosToRemove, ...videosToRemove];
    if (allFileNamesToRemove.length > 0) _params.photosToRemove = allFileNamesToRemove;

    const response = await this.dataLayer({
        url: url,
        params: _params,
        cacheAge: 0,
        userToken: this.userToken
    });

    if (response.status === 'OK') {
        if (videosToAdd.length > 0) {
            const [videoOrderYear, videoOrderNumber, videoOrderId] = selected.orderID.split('-');
            await this.damageReportUploadVideos({
                orderYear: Number(videoOrderYear),
                orderNumber: videoOrderNumber,
                orderId: Number(videoOrderId),
                packageId: selected.packageID,
                component: _params.component,
                videos: toJS(videosToAdd),
            });
        }

        this.damageReportResetToEmpty();
        const [orderYear, orderNumber, orderId] = this.damageReport.selected.orderID
            ? this.damageReport.selected.orderID.split('-')
            : selected.orderID?.split('-') || [];

        if (isStandalone) {
            await this.damageReportGetDamagesByPackage();
        } else {
            await this.damageReportGetDamagesByOrder({
                n_anno: Number(orderYear),
                c_numeratore: orderNumber,
                n_ordine: Number(orderId)
            });
        }
        this.notificationAdd(
            langstore.t('damage_report_updated', 'Segnalazione modificata con successo'),
            'damage_updated',
            10000,
            'warning'
        );
    }
};

export {
    damageReportRetrieveDetailsByUrlParams,
    damageReportGetAllData,
    damageReportGetOrderRows,
    damageReportGetDamageTypes,
    damageReportGetComponents,
    damageReportGetDamagesByOrder,
    damageReportAssemblerEditStatus,
    damageReportAssemblerEditStatusToggler,
    damageReportSetSelectOrderId,
    damageReportSetSelectPackageId,
    damageReportSetSelectComponent,
    damageReportResetToEmpty,
    damageReportAddPhoto,
    damageReportRemovePhoto,
    damageReportAddVideo,
    damageReportRemoveVideo,
    damageReportUploadVideos,
    damageReportRemoveComponentReport,
    damageReportSetDamageType,
    damageReportSetNotes,
    damageReportSubmit,
    damageReportSubmitToSupport,
    damageReportGetDamagesByPackage,
    damageReportGetDamagesByComponents,
    damageReportLoadComponentReport,
    damageReportUpdate
};
